import { MessageCircle, Poll } from '@/components/icons';
import {
  topicLabel,
  type PostListItemDto,
  regionLabel,
  GRANT_SUPPORT_FILTERS,
  SUPPORT_VIEW_LABEL,
  supportHeadline,
  type GrantLatestByType,
  type GrantListItemDto,
  type GrantSupportFilter,
} from '@zoxo/shared';

import { formatDate } from '../articles/format';
import { TYPE_PATH } from '../grants/type-routes';
import { cn } from '../../lib/utils';
import { SUPPORT_BADGE, SUPPORT_PILL } from '../grants/grant-parts';

/**
 * 랜딩(routes/index)의 유형별 최신 네 칸 · 커뮤니티 두 칸.
 */

/**
 * **테두리는 유형별로 달라지지 않는다.** 네 칸이 각기 다른 색 테두리를 두르면
 * 테두리가 스스로 뜻을 가진 것처럼 보이는데, 유형을 말하는 것은 왼쪽 위 칩
 * 하나면 충분하다. 색은 칩에만 두고 테두리는 기본 회색(`--color-border`)으로
 * 통일한다.
 */
const PANEL_ORDER: readonly GrantSupportFilter[] = GRANT_SUPPORT_FILTERS;

/** 패널 한 줄의 제목 — 연도만 뗀다. 지역은 제목이 아니라 옆의 라벨이 말한다. */
export function panelTitle(g: Pick<GrantListItemDto, 'title'>): string {
  return g.title.replace(/\d{4}년 /g, '').trim();
}

/**
 * **네 유형을 나란히 놓는다.**
 *
 * 목록 하나에 필터로 묻어 두면 「융자만 보기」가 두 번 클릭 깊이에 숨는다.
 * 보조금을 찾는 사람과 융자를 찾는 사람은 다른 사람이고, 첫 화면에서 자기
 * 칸을 바로 찾을 수 있어야 한다.
 *
 * **각 줄이 금액을 말한다.** 제목만 늘어놓으면 다섯 줄을 다 열어 봐야 한다 —
 * 그건 우리가 대신 해 주기로 한 일이다.
 *
 * 유형별 행이 하나도 없으면 그 칸을 **안 그린다.** 「없음」이라고 적힌 빈 칸
 * 넷은 화면을 채우기만 하고 아무것도 안 알려준다.
 */
export function LatestPanels({ latest }: { latest: GrantLatestByType | undefined }) {
  const panels = PANEL_ORDER.map((k) => ({ k, items: latest?.[k] ?? [] })).filter(
    (p) => p.items.length > 0,
  );
  if (panels.length === 0) return null;

  return (
    // grid-cols-1 이 없으면 암시적 트랙이 max-content 로 벌어져 모바일에서
    // 카드가 화면 밖까지 자란다 (390px 에서 629px 로 실측). minmax(0,1fr) 가
    // 트랙을 컨테이너 폭에 가둔다.
    <section className="grid grid-cols-1 gap-3 sm:grid-cols-2">
      {panels.map(({ k, items }) => (
        <div key={k} className="rounded-xl border border-border bg-white p-4">
          <div className="mb-2 flex items-baseline justify-between">
            <a href={TYPE_PATH[k]} className="group inline-flex items-baseline gap-2">
              <span
                className={cn(SUPPORT_PILL, SUPPORT_BADGE[k])}
              >
                {SUPPORT_VIEW_LABEL[k]}
              </span>
              <span className="text-sm font-semibold text-gray-900 group-hover:text-[#18BB69]">
                최신 {items.length}건
              </span>
            </a>
            <a href={TYPE_PATH[k]} className="text-xs text-gray-400 hover:text-[#18BB69]">
              더 보기
            </a>
          </div>

          <ul>
            {items.map((g) => {
              const head = supportHeadline(k, g.support[k] ?? { n: 0 });
              return (
                <li key={g.id} className="border-b border-gray-50 last:border-b-0">
                  <a
                    // 눌러 들어온 칸이 상세 상단 알약에 켜진다(news.$id `?tab=`).
                    href={`/news/${g.id}?tab=${k}`}
                    className="flex items-baseline gap-2 py-1.5 hover:text-[#18BB69]"
                  >
                    {regionLabel(g) ? (
                      <span className="shrink-0 text-[11px] text-gray-400">{regionLabel(g)}</span>
                    ) : null}
                    <span className="min-w-0 flex-1 truncate text-[13px] leading-snug text-gray-800">
                      {panelTitle(g)}
                    </span>
                    {/*
                      금액이 오른쪽에 선다. 제목이 길면 잘리지만 금액은 안
                      잘린다 — 이 줄에서 독자가 먼저 보는 것이 그 숫자다.
                    */}
                    {head ? (
                      <span className="shrink-0 text-[11px] font-semibold tabular-nums text-gray-500">
                        {head}
                      </span>
                    ) : null}
                  </a>
                </li>
              );
            })}
          </ul>
        </div>
      ))}
    </section>
  );
}

/**
 * **커뮤니티 한 칸** — 지원사업 네 칸(LatestPanels)과 같은 카드·같은 줄 리듬. 전체 게시판 최신 5건.
 * 줄: 게시판(왼쪽 작은 회색) · 제목(가운데, 넘치면 말줄임) · 글쓴이 · 설문 아이콘 · 댓글 수(오른쪽).
 * 글이 없으면 칸을 안 그린다.
 */
export function CommunityPanel({ label, posts }: { label: string; posts: PostListItemDto[] | undefined }) {
  if (!posts || posts.length === 0) return null;
  return (
    <section aria-label={`커뮤니티 ${label}`} className="rounded-xl border border-border bg-white p-4">
      <div className="mb-2 flex items-baseline justify-between">
        <a href="/posts" className="group inline-flex items-baseline gap-2">
          {/* 바로 위 지원사업 칸의 기둥 알약(SUPPORT_PILL)과 같은 모양·크기. 색만 검정. */}
          <span className={cn(SUPPORT_PILL, 'bg-gray-900 text-white')}>커뮤니티</span>
          <span className="text-sm font-semibold text-gray-900 group-hover:text-brand">{label} {posts.length}건</span>
        </a>
        <a href="/posts" className="text-xs text-gray-400 hover:text-brand">
          더 보기
        </a>
      </div>
      {/* 줄 모양은 커뮤니티 목록(features/community/community-list.tsx)과 같다 — 왼쪽 제목(굵게)+설문·댓글, 오른쪽 게시판 알약 · 글쓴이 · 날짜. */}
      <ul>
        {posts.map((p) => (
          <li key={p.id}>
            {/* 홈에서는 모바일도 한 줄 — 제목이 말줄임으로 줄고 오른쪽(게시판·글쓴이·날짜)은 안 줄어든다. 날짜는 sm 이상에서만. 세로 여백은 지원사업 칸과 같은 6px. */}
            <a href={`/posts/${p.id}`} className="group flex items-baseline justify-between gap-3 py-1.5 sm:gap-6">
              {/* 제목 글자는 지원사업 칸의 줄과 같은 13px. */}
              <span className="min-w-0 truncate text-[13px] leading-snug text-gray-800 group-hover:text-brand">
                {p.title}
                {p.hasSurvey ? <Poll className="ml-1.5 inline size-3 align-middle text-brand" aria-label="설문 있음" /> : null}
                {p.commentCount > 0 ? (
                  <span className="ml-1.5 inline-flex items-center gap-0.5 align-middle text-[11px] font-semibold tabular-nums text-brand">
                    <MessageCircle className="size-3" aria-hidden="true" />
                    <span aria-label={`댓글 ${p.commentCount}개`}>({p.commentCount})</span>
                  </span>
                ) : null}
              </span>
              <span className="flex shrink-0 items-center gap-1.5 whitespace-nowrap text-xs text-text-subtle">
                {p.topic ? (
                  <span className="inline-flex items-center rounded-full border border-gray-200 bg-white px-2 py-0.5 text-xs font-medium text-gray-600">
                    {topicLabel(p.topic)}
                  </span>
                ) : null}
                <span className="font-medium text-text-muted">{p.author.nickname}</span>
                <span aria-hidden="true" className="hidden sm:inline">·</span>
                <span className="hidden sm:inline">{formatDate(p.createdAt)}</span>
              </span>
            </a>
          </li>
        ))}
      </ul>
    </section>
  );
}
